var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { responseHandler } from '@jive/common';
import LifetimeChannel from './lifetimeChannel';
import ConfigsManager from '../modules/config/configs';
import { LogFactory } from '../modules/config/logging';
import Analytics from '../modules/config/analytics';
import { merge, EMPTY as empty, fromEvent, of, throwError, Subject } from 'rxjs';
import { catchError, map, mergeMap, retryWhen, scan, tap } from 'rxjs/operators';
export var NOTIFICATION_CHANNEL_UNEXPECTED_EVENT = 'NOTIFICATION_CHANNEL_UNEXPECTED_EVENT';
export var NOTIFICATION_CHANNEL_EXPIRATION_ERROR = 'NOTIFICATION_EXPIRATION_ERROR';
export var NOTIFICATION_CHANNEL_REFRESHED = 'NOTIFICATION_REFRESHED';
export var NOTIFICATION_CHANNEL_CONNECTED_EVENT = 'NOTIFICATION_CHANNEL_CONNECTED_EVENT';
export var NOTIFICATION_CHANNEL_DISCONNECTED_EVENT = 'NOTIFICATION_CHANNEL_DISCONNECTED_EVENT';
export var NOTIFICATION_CHANNEL_ERROR_EVENT = 'NOTIFICATION_CHANNEL_ERROR_EVENT';
export var NOTIFICATION_CHANNEL_RECONNECTED_EVENT = 'NOTIFICATION_CHANNEL_RECONNECTED_EVENT';
export var NOTIFICATION_CHANNEL_MESSAGE_EVENT = 'NOTIFICATION_CHANNEL_MESSAGE_EVENT';
export var NOTIFICATION_CHANNEL_OFFLINE_EVENT = 'NOTIFICATION_CHANNEL_OFFLINE_EVENT';
export var NOTIFICATION_CHANNEL_ONLINE_EVENT = 'NOTIFICATION_CHANNEL_ONLINE_EVENT';
export var NOTIFICATION_CHANNEL_SERVER_DOWN = 'NOTIFICATION_CHANNEL_SERVER_DOWN';
var Logger = LogFactory('NotificationChannel');
var NotificationChannel = /** @class */ (function (_super) {
    __extends(NotificationChannel, _super);
    /**
     * Create a notification channel
     *
     * @param {string} userId Jive user id
     * @param {JiveHttpHandler} jiveHttpHandler Jive's HttpHandler
     */
    function NotificationChannel(userId, jiveHttpHandler) {
        var _this = _super.call(this) || this;
        _this.userId = userId;
        _this.jiveHttpHandler = jiveHttpHandler;
        _this.id = '';
        _this._lifetime = 0;
        _this.channelInfo$ = new Subject();
        _this.refreshUrlCallback = _this.create.bind(_this);
        return _this;
    }
    Object.defineProperty(NotificationChannel.prototype, "lifetime", {
        get: function () {
            return this._computeLifetime(this._lifetime);
        },
        set: function (l) {
            this._lifetime = l;
        },
        enumerable: true,
        configurable: true
    });
    NotificationChannel.prototype.getChannelInfo = function () {
        return this.jiveHttpHandler.post(ConfigsManager.notificationUrl + "/channels/" + this.userId, { channelLifetime: ConfigsManager.DEFAULT_NOTIFICATION_CHANNEL_LIFETIME });
    };
    NotificationChannel.prototype.refreshLifetime = function (channelId) {
        return this.jiveHttpHandler.put(ConfigsManager.notificationUrl + "/channels/" + this.userId + "/" + channelId + "/channelLifetime", null);
    };
    NotificationChannel.prototype.create = function () {
        var _this = this;
        return this.getChannelInfo().pipe(map(responseHandler), tap(function (response) {
            var channelId = response.resourceURL.split('/').pop();
            if (!channelId) {
                throw {
                    errorCode: 'INVALID_RESOURCE_URL',
                    message: 'Invalid Resource URL from notification channel'
                };
            }
            _this.id = channelId;
            _this.lifetime = response.channelLifetime;
            _this.url = response.channelData.channelURL;
            _this.channelInfo$.next(response);
        }));
    };
    /**
     * Filter messages received from the channel, and dispatch them to the
     * appropriate place.
     */
    NotificationChannel.prototype.observeMessageEvents = function () {
        return fromEvent(this, this.MESSAGE).pipe(mergeMap(function (message) {
            if (message.event === 'Notification') {
                if (message.data) {
                    return of({ type: message.data.source, payload: message.data });
                }
            }
            var error = {
                errorCode: NOTIFICATION_CHANNEL_UNEXPECTED_EVENT,
                message: "Unexpected event from channel: " + message.event + ", json: " + JSON.stringify(message)
            };
            Logger.error(error);
            Analytics.trackEvent('Javascript Errors', "Error: " + error.errorCode + " - " + error.message);
            return empty;
        }));
    };
    /**
     * Refresh the channel
     */
    NotificationChannel.prototype.refreshChannelLifetime = function () {
        var _this = this;
        // TODO: delay retries, perhaps do a backoff
        return this.refreshLifetime(this.id).pipe(retryWhen(function (errors) { return errors.pipe(scan(function (errorCount, error) {
            if (error.errorCode === '503') { // server is gracefully shutting down
                throw error; // stop retry immediately
            }
            if (errorCount >= 2) {
                throw error; // stop retry
            }
            return errorCount + 1;
        }, 0)); }), catchError(function (error) {
            Analytics.trackEvent('Javascript Errors', "Error: " + error.errorCode + " - " + error.message);
            return throwError({
                errorCode: NOTIFICATION_CHANNEL_EXPIRATION_ERROR,
                message: "Could not refresh the channel: " + JSON.stringify(error)
            });
        }), map(responseHandler), map(function (response) { return response.channelLifetime; }), tap(function (channelLifetime) { return _this.lifetime = channelLifetime; }));
    };
    NotificationChannel.prototype.observeChannelEvents = function () {
        return merge(fromEvent(this, this.MESSAGE).pipe(map(function (e) { return ({ type: NOTIFICATION_CHANNEL_MESSAGE_EVENT, payload: e }); })), fromEvent(this, this.DISCONNECTED).pipe(map(function (e) { return ({ type: NOTIFICATION_CHANNEL_DISCONNECTED_EVENT, payload: e }); })), fromEvent(this, this.ERROR).pipe(map(function (e) { return ({ type: NOTIFICATION_CHANNEL_ERROR_EVENT, payload: e }); })), fromEvent(this, this.CONNECTED).pipe(map(function () { return ({ type: NOTIFICATION_CHANNEL_CONNECTED_EVENT, payload: null }); })), fromEvent(this, this.RECONNECTED).pipe(map(function () { return ({ type: NOTIFICATION_CHANNEL_RECONNECTED_EVENT, payload: null }); })), fromEvent(this, this.OFFLINE).pipe(map(function () { return ({ type: NOTIFICATION_CHANNEL_OFFLINE_EVENT, payload: null }); })), fromEvent(this, this.ONLINE).pipe(map(function () { return ({ type: NOTIFICATION_CHANNEL_ONLINE_EVENT, payload: null }); })), fromEvent(this, this.SERVER_DOWN).pipe(map(function () { return ({ type: NOTIFICATION_CHANNEL_SERVER_DOWN, payload: null }); })));
    };
    NotificationChannel.prototype.observeErrorEvents = function () {
        return fromEvent(this, this.ERROR);
    };
    NotificationChannel.prototype.observeNetworkOnlineEvents = function () {
        return fromEvent(this, this.ONLINE);
    };
    NotificationChannel.prototype.observeNetworkOfflineEvents = function () {
        return fromEvent(this, this.OFFLINE);
    };
    NotificationChannel.prototype.observeConnectedEvents = function () {
        return fromEvent(this, this.CONNECTED);
    };
    NotificationChannel.prototype.observeReconnectedEvents = function () {
        return fromEvent(this, this.RECONNECTED);
    };
    NotificationChannel.prototype.observeDisconnectedEvents = function () {
        return fromEvent(this, this.DISCONNECTED);
    };
    NotificationChannel.prototype.observeServerDownEvents = function () {
        return fromEvent(this, this.SERVER_DOWN);
    };
    NotificationChannel.prototype.observeTerminatedEvents = function () {
        return fromEvent(this, this.TERMINATED);
    };
    /**
     * Compute lifetime of notification channel (expiration)
     * Note: it is not meant to convert seconds to milliseconds!
     *
     * @param {int} l server lifetime in seconds
     * @returns {int} lifetime in milliseconds
     */
    NotificationChannel.prototype._computeLifetime = function (l) {
        return (l * 850); // meant to refresh at 85% to allow buffer.
    };
    return NotificationChannel;
}(LifetimeChannel));
export { NotificationChannel };
export default NotificationChannel;
//# sourceMappingURL=notificationChannel.js.map